/* ==========================================================================
   LURU Cart Controller (_NR rework, 17 Jul)
   One file owns ALL custom cart behavior. Replaces the inline <script>s that
   previously lived inside luru-cart-ux / luru-cart-progress / luru-cart-upsells
   and the double fetch-wrapping from luru-post-add-upsell.

   Why: the drawer is morphed by the theme (Section Rendering), so scripts
   inside the morphed HTML never (re)execute — the progress bar and upsells
   were dead for any visitor who arrived with an empty cart. This file lives
   at the document level, binds by delegation, and re-reads the DOM on every
   sync, so morphing can never kill it.

   Integration points (verified against this theme's own source):
   · cart-drawer-component.open()            — assets/cart-drawer.js:73
   · CustomEvent 'cart:update' with
     detail.data.itemCount / detail.data.source — assets/events.js,
     consumed by cart-icon.js (bubble) and component-cart-items.js
     (re-renders the drawer section server-side, cache:false)
   · Theme-initiated cart calls carry a `sections` payload (FormData or JSON)
     and manage their own UI — we leave those alone except opening the drawer.

   No polling. No setInterval. One /cart.js round-trip per mutation burst.
   ========================================================================== */

(function () {
  'use strict';
  if (window.luruCart) return;

  var origFetch = window.fetch.bind(window);
  var lastCart = null;
  var refreshing = null;
  var refreshTimer = null;
  var resyncTimers = [];

  var API = {
    /* Open the theme's cart drawer through its real API. The component's
       showDialog() defers showModal() into a requestAnimationFrame; the
       300ms fallback covers rAF-starved contexts (hidden tabs) and any
       future theme change that breaks .open(). */
    openDrawer: function () {
      var c = document.querySelector('cart-drawer-component');
      var d = document.querySelector('.cart-drawer__dialog');
      if (c && typeof c.open === 'function') c.open();
      setTimeout(function () {
        if (d && !d.open) { try { d.showModal(); } catch (e) { /* noop */ } }
      }, 300);
      return true;
    },
    refresh: function (notifyTheme) { return refresh(notifyTheme); }
  };
  window.luruCart = API;
  /* Back-compat for older snippets that call window.luruOpenCartDrawer() */
  window.luruOpenCartDrawer = API.openDrawer;

  /* ---------------- cart state ---------------- */

  function dispatchThemeUpdate(cart) {
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'luru-cart',
        data: { itemCount: cart.item_count, source: 'luru-cart' }
      }
    }));
  }

  function refresh(notifyTheme) {
    if (refreshing) return refreshing;
    refreshing = origFetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        refreshing = null;
        lastCart = cart;
        if (notifyTheme) dispatchThemeUpdate(cart); // bubble + drawer re-render
        syncWidgets(cart);
        resyncSoon(); // re-apply widget state after the async section morph lands
        return cart;
      })
      .catch(function () { refreshing = null; });
    return refreshing;
  }

  /* Mutations arrive in bursts (e.g. bundle add + tote). Coalesce. */
  var pendingNotify = false;
  function scheduleRefresh(notifyTheme) {
    pendingNotify = pendingNotify || !!notifyTheme;
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      var n = pendingNotify;
      pendingNotify = false;
      refresh(n);
    }, 150);
  }

  /* The section morph that follows a cart:update is async and replaces the
     widget markup with server defaults — re-paint from cached cart, no network. */
  function resyncSoon() {
    resyncTimers.forEach(clearTimeout);
    resyncTimers = [400, 1000, 2000].map(function (ms) {
      return setTimeout(function () { if (lastCart) syncWidgets(lastCart); }, ms);
    });
  }

  /* ---------------- single fetch wrapper ---------------- */

  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : ((input && input.url) || '');
    var isAdd = url.indexOf('/cart/add') !== -1;
    var isMutation = isAdd || /\/cart\/(change|update|clear)/.test(url);

    /* Theme-initiated requests (product form, drawer qty steppers) carry a
       `sections` render payload and dispatch their own events — don't double
       any of their work. Our widgets always post plain JSON without it. */
    var themeInitiated = false;
    if (isMutation && init && init.body) {
      if (typeof FormData !== 'undefined' && init.body instanceof FormData) themeInitiated = true;
      else if (typeof init.body === 'string' && init.body.indexOf('sections') !== -1) themeInitiated = true;
    }

    var p = origFetch(input, init);
    if (isMutation) {
      p.then(function (res) {
        if (!res || !res.ok) return;
        if (isAdd) API.openDrawer(); // instant feedback — the theme has no auto-open
        if (themeInitiated) {
          /* theme morphs its own UI; we only need widget state */
          scheduleRefresh(false);
        } else {
          /* our widgets added something; theme UI must be told */
          scheduleRefresh(true);
        }
      }).catch(function () { /* never interfere with the page's own handling */ });
    }
    return p;
  };

  /* ---------------- widgets: progress bar + tote + upsells ---------------- */

  function cartUnits(cart, eligibleSet) {
    var units = 0;
    cart.items.forEach(function (i) {
      if (eligibleSet.has(String(i.variant_id))) units += i.quantity;
    });
    return units;
  }

  function syncProgress(cart) {
    var nodes = document.querySelectorAll('[data-luru-cartprog]');
    if (!nodes.length) return;
    nodes.forEach(function (el) {
      var ELIGIBLE = new Set((el.dataset.eligible || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean));
      var MIN = parseInt(el.dataset.min, 10) || 3;
      var units = cartUnits(cart, ELIGIBLE);
      var remaining = Math.max(MIN - units, 0);
      var bar = el.querySelector('[data-cp-bar]');
      var label = el.querySelector('[data-cp-label]');
      if (bar) bar.style.width = Math.min(units / MIN * 100, 100) + '%';
      el.classList.toggle('is-unlocked', remaining === 0);
      if (!label) return;
      if (units === 0) label.textContent = 'Any 3 issues — ₹3,000';
      else if (remaining > 0) label.textContent = 'Add ' + remaining + ' more issue' + (remaining === 1 ? '' : 's') + ' to unlock ₹3,000';
      else label.textContent = '✓ Unlocked — 20% off applied at checkout';
    });
  }

  /* Tote auto-add removed 8 Aug 2026: the free tote now comes only with the
     Box Set or the Next Three (Shopify BXGY zeroes it in-cart); the any-3
     bundle is 20% off with no gift, so the cart never adds lines by itself. */

  function syncUpsells(cart) {
    var ids = cart.items.map(function (i) { return String(i.variant_id); });

    var top = document.querySelector('[data-luru-cup-top]');
    if (top) {
      var ELIGIBLE = new Set((top.dataset.eligible || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean));
      var TRIOS = (top.dataset.trioVariants || '').split(',').map(function (s) { return s.trim(); }).filter(Boolean);
      var units = cartUnits(cart, ELIGIBLE);
      var hasTrio = TRIOS.some(function (v) { return ids.indexOf(v) !== -1; });
      var dismissed = sessionStorage.getItem('luru-cup-top-dismissed') === '1';
      top.hidden = !(units >= 1 && units < 3 && !hasTrio && !dismissed);
    }

    var bottom = document.querySelector('[data-luru-cup-bottom]');
    if (bottom) {
      var b = bottom.querySelector('[data-cup-product="b"]');
      if (b) {
        var bIn = ids.indexOf(String(b.dataset.variant)) !== -1;
        bottom.hidden = cart.item_count === 0 || bIn;
        b.hidden = false;
      }
    }
  }

  function syncWidgets(cart) {
    if (!cart || !cart.items) return;
    lastCart = cart;
    syncProgress(cart);
    syncUpsells(cart);
  }

  /* ---------------- delegated clicks (survive any morph) ---------------- */

  document.addEventListener('click', function (e) {
    var dis = e.target.closest('[data-cup-dismiss]');
    if (dis) {
      e.preventDefault();
      e.stopPropagation();
      sessionStorage.setItem('luru-cup-top-dismissed', '1');
      var card = dis.closest('[data-luru-cup-top]');
      if (card) card.hidden = true;
      return;
    }
    var add = e.target.closest('[data-cup-add]');
    if (add) {
      e.preventDefault();
      e.stopPropagation();
      var product = add.closest('[data-cup-product]');
      if (!product || !product.dataset.variant) return;
      add.textContent = '…';
      window.fetch('/cart/add.js', { /* wrapped on purpose: drawer stays open, refresh scheduled */
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: +product.dataset.variant, quantity: 1 }] })
      }).then(function (r) {
        add.textContent = r.ok ? '✓' : '!';
        setTimeout(function () { add.textContent = 'Add'; }, 1500);
      });
    }
  }, true);

  /* ---------------- sync triggers (all event-driven, zero polling) ---------------- */

  document.addEventListener('cart:update', function (e) {
    if (e.detail && e.detail.sourceId === 'luru-cart') return; // our own dispatch
    var cart = e.detail && e.detail.resource;
    if (cart && cart.items) { syncWidgets(cart); resyncSoon(); }
    else scheduleRefresh(false);
  });
  document.addEventListener('cart:refresh', function () { scheduleRefresh(false); }); // legacy event from older snippets
  document.addEventListener('dialog:open', function () { if (lastCart) syncWidgets(lastCart); else scheduleRefresh(false); });
  window.addEventListener('pageshow', function () { scheduleRefresh(false); }); // bfcache restores
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { refresh(false); });
  else refresh(false);
})();
